import React, { useState, useEffect } from 'react';

export const TeacherUpcomingSession = ({ sessions = [], batchDetails = {} }) => {
  const [loading, setLoading] = useState(false); // No internal fetch anymore

  useEffect(() => {
    setLoading(false);
  }, [sessions, batchDetails]);

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getSessionStatus = (dateStr, endTimeStr) => {
    const now = new Date();
    const sessionDate = new Date(dateStr);
    const [endHour, endMin] = endTimeStr.split(':').map(Number);
    sessionDate.setHours(endHour, endMin, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sessionDateOnly = new Date(dateStr);
    sessionDateOnly.setHours(0, 0, 0, 0);

    if (sessionDateOnly > today) return 'upcoming';
    if (sessionDateOnly < today) return 'past';
    // same date: check if end_time has passed
    return now >= sessionDate ? 'completed' : 'today';
  };

  if (!sessions || sessions.length === 0) {
    return (
      <div className="col-12">
        <div className="card blueborder rounded-3">
          <div className="card-header blue-card-header border-bottom-0">
            <h5 className="fw-bold mb-0 newfont d-flex align-items-center gap-2">
              <i className="bi bi-calendar-week me-2"></i>
              Upcoming Sessions This Week
            </h5>
          </div>
          <div className="card-body text-center py-5">
            <img src="/assets/images/nosession-today.png" alt="No Session" className="img-fluid" style={{ maxHeight: "100px" }} />
            <p className="text-muted mt-3">No upcoming sessions this week</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="col-12">
        <div className="card blueborder rounded-4 ">
          <div className="card-header blue-card-header border-bottom-0">
            <h5 className="fw-bold mb-0 newfont d-flex align-items-center gap-2">
              <i className="bi bi-calendar-week"></i>
              Upcoming Sessions This Week
            </h5>
          </div>
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-12">
      <div className="card blueborder rounded-3">
        <div className="card-header blue-card-header border-bottom-0">
          <h5 className="fw-bold mb-0 newfont d-flex align-items-center gap-2">
            <i className="bi bi-calendar-week me-2"></i>
            Upcoming Sessions This Week
          </h5>
        </div>
        <div className="card-body p-0 d-none d-md-block">
          {/* Desktop table view */}
          <div className="table-responsive">
            <table className="table table-hover mb-0 align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="fw-semibold text-dark">Date & Time</th>
                  <th className=" fw-semibold text-dark">Day</th>
                  <th className="fw-semibold text-dark">Batch</th>
                  <th className="fw-semibold text-dark">Program</th>
                  <th className="text-center fw-semibold text-dark">Status</th>
                  <th className="text-center fw-semibold text-dark">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => {
                  const batchInfo = batchDetails[session.batch_id] || {};
                  const sessionStatus = getSessionStatus(session.date, session.end_time);
                  const isJoinDisabled = sessionStatus !== 'today';
                  return (
                    <tr key={session.id} className="teacher-upcoming-session-row">
                      <td>
                        <div>
                          <div className="fw-semibold text-dark mb-1">{formatDate(session.date)}</div>
                          <div className="small text-secondary">
                            {formatTime(session.start_time)} - {formatTime(session.end_time)}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge teacher-upcoming-session-primary-bg teacher-upcoming-session-primary-text px-3 py-2 rounded-pill text-capitalize">
                          {session.day}
                        </span>
                      </td>
                      <td>
                        <div className="fw-semibold text-dark">
                          {batchInfo.batch_name || `Batch #${session.batch_id}`}
                        </div>
                      </td>
                      <td>
                        <div className='text-dark'>
                          {batchInfo.program_title || `Course #${session.course_id}`}
                        </div>
                      </td>
                      <td className="text-center">
                        {session.postpone_status === 1 ? (
                          <span className="badge bg-warning text-dark px-3 py-2 rounded-pill fw-semibold">
                            Postponed
                          </span>
                        ) : (
                          <span className="badge teacher-upcoming-session-success-bg teacher-upcoming-session-success-text px-3 py-2 rounded-pill fw-semibold">
                            {sessionStatus === 'completed' ? 'Completed' : 'Scheduled'}
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-2 justify-content-center">
                          {session.meet_url && session.postpone_status !== 1 ? (
                            <button
                              className={`btn btn-sm ${isJoinDisabled ? 'btn-outline-secondary disabled' : 'btn-outline-success'}`}
                              disabled
                              title={isJoinDisabled ? (sessionStatus === 'past' || sessionStatus === 'completed' ? 'Session date has passed' : 'Join will be available on the session date') : 'Join Meet'}
                            >
                              {sessionStatus === 'completed' ? 'Completed' : (isJoinDisabled ? (sessionStatus === 'past' ? 'Expired' : 'Upcoming') : 'Join Meet')}
                            </button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-body p-0 d-md-none">
          {/* Mobile card view */}
          <div className="list-group list-group-flush">
            {sessions.map((session) => {
              const batchInfo = batchDetails[session.batch_id] || {};
              const sessionStatus = getSessionStatus(session.date, session.end_time);
              const isJoinDisabled = sessionStatus !== 'today';
              return (
                <div key={session.id} className="list-group-item">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div className="fw-semibold text-dark">{formatDate(session.date)}</div>
                      <div className="small text-secondary">
                        {formatTime(session.start_time)} - {formatTime(session.end_time)}
                      </div>
                    </div>
                    <span className="badge teacher-upcoming-session-primary-bg teacher-upcoming-session-primary-text px-2 py-1 rounded-pill text-capitalize">
                      {session.day}
                    </span>
                  </div>
                  <div className="mb-2">
                    <div className="fw-semibold text-dark">
                      {batchInfo.batch_name || `Batch #${session.batch_id}`}
                    </div>
                    <div className='text-dark small'>
                      {batchInfo.program_title || `Course #${session.course_id}`}
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      {session.postpone_status === 1 ? (
                        <span className="badge bg-warning text-dark px-2 py-1 rounded-pill fw-semibold">
                          Postponed
                        </span>
                      ) : (
                        <span className="badge teacher-upcoming-session-success-bg teacher-upcoming-session-success-text px-2 py-1 rounded-pill fw-semibold">
                          {sessionStatus === 'completed' ? 'Completed' : 'Scheduled'}
                        </span>
                      )}
                    </div>
                    <div>
                      {session.meet_url && session.postpone_status !== 1 ? (
                        <a
                          href={session.meet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`btn btn-sm ${isJoinDisabled ? 'btn-outline-secondary disabled' : 'btn-outline-success'}`}
                          onClick={e => isJoinDisabled && e.preventDefault()}
                          aria-disabled={isJoinDisabled}
                          title={isJoinDisabled ? (sessionStatus === 'past' || sessionStatus === 'completed' ? 'Session date has passed' : 'Join will be available on the session date') : 'Join Meet'}
                        >
                          {sessionStatus === 'completed' ? 'Completed' : (isJoinDisabled ? (sessionStatus === 'past' ? 'Expired' : 'Upcoming') : 'Join Meet')}
                        </a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};